import { DG } from '@cotera/sdk/core';
import { assertOk } from '@cotera/utilities';
import { StorageConnection } from './storage-connection';

/**
 * A connection to a persistent(high-level) datstructure that implements:
 * - An increasable non-empty set of branch ids.
 * - A mutable list of staged operations associated with each branch id.
 * - A mutable never-null value denoting the "current" branch id.
 *
 * Gaurantees that errors will not be thrown when any methods are called.
 *
 * The set of branch ids will always contain a "main" branch that is
 * initially associated with 0 operations and is the initial current branch.
 *
 * Note: This Javascript class does not contain the data of the User's branches,
 * but rather a connection to a persistent data structure that contains said data.
 * (Hence, this class is named UserBranchesConnection instead of UserBranchesData.)
 * Therefore, instantiation of this class does not reset storage, but rather
 * interfaces as a continuation to the previous session's storage.
 */
export class UserBranchesConnection {
  private constructor(
    private readonly orgName: string,
    private readonly storage: StorageConnection
  ) {}

  /**
   * Creates an instance of UserBranchesConnection.
   *
   *  Note: Instantiation of this class does not reset storage, but rather
   * interfaces as a continuation to the previous session's storage.
   *
   * Adds a `main` branch (and sets it as current) if a main branch does not exist.
   */
  static async create(orgName: string, storage: StorageConnection) {
    if (!(await storage.hasBranchID('main'))) {
      // Gauranteed to succeed because main is not a branch yet
      const _1: 'ok' = assertOk(await storage.addBranchID('main'));

      // Gauranteed to succeed because:
      // If main is not a branch yet, then currentID has never been set.
      const _2: 'ok' = assertOk(await storage.setCurrentBranchID('main'));
    }

    return new UserBranchesConnection(orgName, storage);
  }

  /**
   * Returns the list of branch ids.
   */
  async getBranchIDs(): Promise<string[]> {
    return this.storage.getBranchIDs();
  }

  /**
   * Creates a new branch, whose ID is of format: `${this.orgName}-${Date.now()}`
   * And sets the new branch as the current branch.
   */
  async newBranchAndSetCurrent(): Promise<void> {
    const newBranchID = `${this.orgName}-${Date.now()}`; // Guaranteed to be unique

    // Gauranteed to succeed because newBranchID is unique
    const _1: 'ok' = assertOk(await this.storage.addBranchID(newBranchID));

    // Gauranteed to succeed because it was a previously added branch
    const _2: 'ok' = assertOk(
      await this.storage.setCurrentBranchID(newBranchID)
    );

    return;
  }

  /**
   * Returns the current branch id.
   *
   * Guaranteed to produce a valid branch id
   * because the main branch is initially the current branch, and
   * subsequent setCurrentBranchID calls are only called on valid branch ids.
   */
  async getCurrentBranchID(): Promise<string> {
    const currentBranch = await this.storage.getCurrentBranchID();
    return currentBranch!; // Gauranteed to be defined because constructor added the main branch.
  }

  /**
   * Returns the list of staged operations associated with the current branch.
   */
  async getStagedOperations(): Promise<DG.OperationData[]> {
    const currentBranch = await this.getCurrentBranchID(); // gauranteed to return an existing branch

    // gaurantee above justifies assertion below
    return assertOk(await this.storage.getOperations(currentBranch));
  }

  /**
   * Appends `operations` to the list of staged operations associated with the current branch.
   */
  async addStagedOperations(operations: DG.OperationData[]): Promise<void> {
    const currentBranch = await this.getCurrentBranchID(); // gauranteed to return an existing branch
    // gaurantee above justifies assertion below
    const _: 'ok' = assertOk(
      await this.storage.addOperations(currentBranch, operations)
    );

    return;
  }

  /**
   * Resets (makes empty) the list of staged operations associated with the current branch.
   */
  async clearStagedOperations(): Promise<void> {
    const currentBranch = await this.getCurrentBranchID(); // gauranteed to return an existing branch

    // Gauranteed to work because current branch is valid.
    const _: 'ok' = assertOk(await this.storage.clearOperations(currentBranch));

    return;
  }
}
